/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { HealthCheckModel } from '../../../../shared/models/healthcheck.model';
import { HealthChecksApiService, LbErrorService, LbRbacService } from '../../../../shared';
import { LanguageService, LogService, ModalService, NotifyService, UtilsService } from '../../../../../../core/services';
import { Observable } from 'rxjs';
import { nameValidation } from '../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-health-check',
    templateUrl: './add-edit-health-checks.component.html',
    styleUrls: ['./add-edit-health-checks.component.scss']
})
export class AddEditHealthChecksComponent extends BaseComponent {
    /**
     * Title action e.g. 'Add', 'Edit', 'View' or 'Duplicate'
     */
    public titleAction: string = 'add';

    /**
     * Hold the health checks for lookups.
     */
    public healthChecks: HealthCheckModel[] = [];

    /**
     * Store the selected healthcheck for edit and view mode.
     */
    public selectedHealthCheck: HealthCheckModel;

    /**
     * The list of available services and there checkbox state.
     */
    public healthCheckServices = [
        // {
        //     name: 'layer-4',
        //     selected: false,
        // },
        {
            name: 'layer-7',
            selected: false,
        },
        {
            name: 'gslb',
            selected: false,
        },
    ];

    /**
     * Form to create and edit health checks.
     */
    public healthCheckForm: FormGroup;

    /**
     * Set if we're in edit mode.
     */
    public editMode: boolean = false;

    /**
     * Set if we're in view mode.
     */
    public viewMode: boolean = false;

    /**
     * Set if we're in duplicate mode.
     */
    public duplicateMode: boolean = false;

    /**
     * The path back to health checks will change depending on whether you are editing or creating a new health check.
     */
    public redirectUrl: string = '../';

    /**
     * Store individual services.
     * @private
     */
    private _services: FormArray;

    /**
     * To store the page lookup for this component.
     * @private
     */
    private pageLookUp: any = {};

    constructor(
        public modalService: ModalService,
        public rbacService: LbRbacService,
        private formBuilder: FormBuilder,
        private healthChecksApiService: HealthChecksApiService,
        private languageService: LanguageService,
        private lbErrorService: LbErrorService,
        private logService: LogService,
        private notifyService: NotifyService,
        private route: ActivatedRoute,
        private router: Router,
        private utilsService: UtilsService,
    ) {
        super();

        this.setupPageType();
        this.setPageTitleAction();
        this.setUpPageData();
        this.setupForm();
        this.subscribeToFormChanges();
    }

    /**
     * Getter for services in the view.
     */
    get services(): FormArray {
        this._services = this.healthCheckForm.get('services') as FormArray;

        return this._services;
    }

    /**
     * Returns form control as FormControl.
     * @param control
     */
    public getFormControl(control: any): FormControl {
        return control.controls.selected as FormControl;
    }

    /**
     * Submit healthcheck to API.
     */
    public submitHealthCheck(): Observable<any> {
        return new Observable(observer => {
            const healthCheck = this.getFormattedFormValues();

            const apiCall = this.editMode && !this.duplicateMode
                ? this.healthChecksApiService.editHealthCheck(healthCheck.id, healthCheck)
                : this.healthChecksApiService.addHealthCheck(healthCheck);

            apiCall
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                        this.router.navigate([this.redirectUrl], { relativeTo: this.route }).then(() => {
                        });
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.healthCheckForm, error.message);
                        this.logService.error(
                            'AddEditHealthChecksComponent:submitHealthcheck', error.message
                        );
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get formatted form values.
     * @private
     */
    private getFormattedFormValues(): HealthCheckModel {
        const scriptForm = this.healthCheckForm.value;

        return {
            id: scriptForm.id ? scriptForm.id : null,
            name: scriptForm.name,
            description: scriptForm.description,
            script: scriptForm.script,
            services: this.getServiceNamesFromForm(scriptForm),
            builtin: false,
            parameters: []
        };
    }

    /**
     * Get service name from form values.
     * @param scriptForm
     * @private
     */
    private getServiceNamesFromForm(scriptForm: any): string[] {
        const selected = scriptForm.services.filter((item: any) => item.selected);

        return selected.map((item: any) => item.name);
    }

    /**
     * Set up page type e.g. add, edit etc.
     * @private
     */
    private setupPageType(): void {
        const path = this.utilsService.getObjectValue(this.route, 'snapshot.url[0].path');
        this.duplicateMode = path && path.includes('duplicate');
        this.editMode = path && path.includes('edit');
        this.viewMode = path && path.includes('view');
    }

    /**
     * Set page title action based on page type.
     * @private
     */
    private setPageTitleAction(): void {
        this.titleAction = this.duplicateMode ? 'duplicate' : this.editMode ? 'edit' : this.viewMode ? 'view' : 'add';
    }

    /**
     * Get page data from api.
     * @private
     */
    private setUpPageData(): void {
        this.healthChecksApiService.getAllHealthChecks()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any[]): void => {
                    this.healthChecks = data;

                    const id = this.route.snapshot.paramMap.get('id');
                    if (id) {
                        this.redirectUrl = '../../';
                        this.getHealthCheckById(id);
                    }
                }
            });
    }

    /**
     * Get healthcheck by ID.
     * @private
     */
    private getHealthCheckById(id: string): void {
        this.healthChecksApiService.getHealthCheckById(id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    this.selectedHealthCheck = data;
                    this.patchHealthCheckToForm(data);
                },
                error: (error: ErrorResponseModel): void => {
                    this.notifyService.error('Could not retrieve data - ', error.message);
                }
            });
    }

    /**
     * Patch healthCheck to form.
     * @param healthCheck
     * @private
     */
    private patchHealthCheckToForm(healthCheck: HealthCheckModel): void {
        this.healthCheckForm.patchValue({
            id: this.duplicateMode ? null : healthCheck.id,
            name: this.duplicateMode ? null : healthCheck.name,
            description: healthCheck.description,
            services: this.patchServicesValues(healthCheck.services),
            script: healthCheck.script
        });
    }

    /**
     * Patch services values.
     * @param services
     * @private
     */
    private patchServicesValues(services: any): FormGroup[] {
        const formGroups = [];
        for (const service of this.healthCheckServices) {
            formGroups.push({
                name: service.name,
                selected: services.includes(service.name)
            });
        }

        return formGroups;
    }

    /**
     * Setup health check script form.
     * @private
     */
    private setupForm(): void {
        this.healthCheckForm = this.formBuilder.group({
            id: [],
            name: ['', [Validators.required, nameValidation]],
            description: [''],
            services: this.formBuilder.array(this.setupServiceFormGroups()),
            script: ['', Validators.required],
        });
    }

    /**
     * Set up the services form group.
     * @private
     */
    private setupServiceFormGroups(): FormGroup[] {
        const formGroups = [];
        for (const service of this.healthCheckServices) {
            formGroups.push(this.formBuilder.group({
                name: service.name,
                viewName: this.formatServiceNameCase(service.name),
                selected: [service.selected, Validators.requiredTrue]
            }));
        }

        return formGroups;
    }

    /**
     * Format the names of the services with the correct case.
     * @param name
     * @private
     */
    private formatServiceNameCase(name: string): string {
        let formattedName: string;
        if (name === 'gslb') {
            formattedName = name.toUpperCase();
        } else {
            formattedName = name.charAt(0).toUpperCase() + name.slice(1);
        }

        return formattedName;
    }

    /**
     * Subscribe to change's in the 'name' input field.
     * @private
     */
    private subscribeToFormChanges(): void {
        this.healthCheckForm.get('name').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: value => {
                    this.utilsService.debounce('nameValue', () => {
                        this.validateName(value);
                    }, 500);
                }
            });

        this.healthCheckForm.get('services').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: values => {
                    this.setAndClearValidators(values);
                }
            });
    }

    /**
     * Validate healthcheck name.
     * @param name
     * @private
     */
    private validateName(name: any): void {
        const id = this.healthCheckForm.get('id').value;
        const matchingName = this.healthChecks.find(check => {
            const matchName = check.name;
            const matchId = check.id;

            return matchName === name && matchId !== id;
        });

        if (!matchingName) {
            return;
        } else {
            this.healthCheckForm.get('name').setErrors({ 'duplicateName': `${this.languageService.translate('duplicate_name')}` });
        }
    }

    /**
     * If either checkbox is checked, clear the validators.
     * @param values
     * @private
     */
    private setAndClearValidators(values: any[]): void {
        const selected = values.find(item => item.selected === true);
        const services = this.healthCheckForm.get('services')['controls'];
        for (const service of services) {
            if (selected) {
                service.get('selected').clearValidators();
            } else {
                service.get('selected').setValidators(Validators.requiredTrue);
            }
            service.get('selected').updateValueAndValidity({ emitEvent: false });
        }
    }
}
